import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  listLivestock,
  getLivestock,
  createLivestock,
  updateLivestock,
  patchLivestock,
  deleteLivestock,
  type LivestockRecord,
  type CreateLivestockInput,
  type UpdateLivestockInput,
  type PatchLivestockInput,
  type ListLivestockParams,
} from '../services/livestockService';

// ─── Query Key Factory ────────────────────────────────────────────────────────

export const livestockKeys = {
  all: (aquariumId: string) => ['livestock', aquariumId] as const,
  list: (aquariumId: string, params?: ListLivestockParams) =>
    ['livestock', aquariumId, 'list', params] as const,
  detail: (aquariumId: string, id: string) =>
    ['livestock', aquariumId, 'detail', id] as const,
};

// ─── Query Hooks ──────────────────────────────────────────────────────────────

export function useLivestockList(
  aquariumId: string,
  params?: ListLivestockParams,
): UseQueryResult<LivestockRecord[], Error> {
  return useQuery({
    queryKey: livestockKeys.list(aquariumId, params),
    queryFn: () => listLivestock(aquariumId, params),
    enabled: Boolean(aquariumId),
  });
}

export function useLivestockItem(
  aquariumId: string,
  id: string,
): UseQueryResult<LivestockRecord, Error> {
  return useQuery({
    queryKey: livestockKeys.detail(aquariumId, id),
    queryFn: () => getLivestock(aquariumId, id),
    enabled: Boolean(aquariumId) && Boolean(id),
  });
}

// ─── Mutation Hooks ───────────────────────────────────────────────────────────

export function useCreateLivestock(
  aquariumId: string,
): UseMutationResult<LivestockRecord, Error, CreateLivestockInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateLivestockInput) => createLivestock(aquariumId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: livestockKeys.all(aquariumId),
      });
    },
  });
}

export function useUpdateLivestock(
  aquariumId: string,
  id: string,
): UseMutationResult<LivestockRecord, Error, UpdateLivestockInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateLivestockInput) => updateLivestock(aquariumId, id, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(livestockKeys.detail(aquariumId, id), updated);
      void queryClient.invalidateQueries({
        queryKey: livestockKeys.all(aquariumId),
      });
    },
  });
}

export function usePatchLivestock(
  aquariumId: string,
  id: string,
): UseMutationResult<LivestockRecord, Error, PatchLivestockInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: PatchLivestockInput) => patchLivestock(aquariumId, id, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(livestockKeys.detail(aquariumId, id), updated);
      void queryClient.invalidateQueries({
        queryKey: livestockKeys.all(aquariumId),
      });
    },
  });
}

export function useDeleteLivestock(
  aquariumId: string,
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteLivestock(aquariumId, id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({
        queryKey: livestockKeys.detail(aquariumId, id),
      });
      void queryClient.invalidateQueries({
        queryKey: livestockKeys.all(aquariumId),
      });
    },
  });
}
